import * as React from 'react';
import { Callout, CommandBar, DefaultButton, Dialog, DirectionalHint, ICommandBarItemProps, Icon, Link, List, PrimaryButton, Spinner, SpinnerSize } from '@fluentui/react';
import { IAssignmentDTO, IContainerSpot, ILayer, IMapData, IRoad, IStackMarker, ITextArea, IWorkingArea, MapDrawingColors, RoadType, WorkingAreaType } from '../../../models';
import { Map, TileLayer, Marker, Popup, LayersControl, FeatureGroup, Polyline, WMSTileLayer, Polygon, Tooltip, ScaleControl } from 'react-leaflet';
import StackDialog from './Dialogs/StackDialog';
import WorkingAreaDialog from './Dialogs/WorkingAreaDialog';
import TextAreaDialog from './Dialogs/TextAreaDialog';
import L, { Direction, LatLng, LatLngTuple } from "leaflet";
import styles from './LeafletMap.module.scss';
import { EditControl } from 'react-leaflet-draw';
import update from 'immutability-helper';
import Utilities, { BackgroundMaps, MapLayers, MapUtilities, OSM_MapLayers, Status } from '../../../helpers/Utilities';
import App from '../../../App';
import AddressAutoComplete from './AddressAutoComplete';
import { debounce } from 'lodash';
import { isMobileOrTablet } from '../../../components/VisibilityControl/UserAgentControl';

//#region Interfaces
export interface ILeafletMapProps {
  currentItem: IAssignmentDTO;
  onDismiss(currentItem: IAssignmentDTO): void;
  onSaveClicked(mapData: IMapData, canEdit: boolean): void;
  onSaveDueToInactivity(mapData: IMapData): void;
}

export interface ILeafletMapState {
  currentItem: IAssignmentDTO;
  mapWidth: number;
  backgroundLayers: ILayer[];
  backgroundLayer: string;
  layers: ILayer[];
  selectedLayers: string[];
  loading: boolean;
  initialUserPosition: LatLngTuple;

  // Drawing elements to persist
  currentWorkingArea?: IWorkingArea;
  currentStack?: IStackMarker;
  currentTextArea?: ITextArea;
  showWorkingAreaDialog: boolean;
  showStackInfoDialog: boolean;
  showDocumentationDialog: boolean;
  showTextAreaDialog: boolean;

  currentZoomLevel: number;
  currentPosition: LatLngTuple;
  mapData: IMapData;

  // User location tracking
  showUserLocationDot: boolean;

  // Drawing
  activePolylineColor: string;
  activeMarkerIcon: L.Icon;
  isDrawing: boolean;
  currentDrawingObjectType: string;
  addedGeometryCount: number;

  canEdit: boolean;

  selectedWorkingAreaId?: string;
  selectedRoadId?: string;
  showFixedTaskPositionPopup?: boolean;
  selectedStackId?: string;
  selectedContainerSpotId?: string;
  selectedTextAreaId?: string;

  // Stack dialog values
  stackDialogValues?: {
    no: string;
    size: number;
    treeSpecices: string;
    tooltipPosition: string;
  };
}

//#region Enums
enum DrawingObjectType {
  None = "None",
  WorkingArea = "WorkingArea",
  RoadAllowed = "RoadAllowed",
  RoadProhibited = "RoadProhibited",
  Stack = "Stack",
  ContainerSpot = "ContainerSpot",
  FixedTaskPosition = "FixedTaskPosition",
  Polyline = "Polyline",
  TextArea = "TextArea"
};

//#region Component
export default class LeafletMap extends React.Component<ILeafletMapProps, ILeafletMapState> {
  private minZoomLevelForLayers = 8;
  private minZoomLevelForDrawingObjects = 7;
  private lineOrBorderWeight = 9;
  private mapRef: any;
  private groupRef: any;
  private editControl: any;
  private userLocationMarker: any;
  private userLocationTimer: any;
  private inactivityTimeout: any;
  private inactivityThreshold = 60000 * 2; //2 minutes idle time
  private userLocationVisibleDuration = 2000; // 2 seconds

  //#region Constructor
  constructor(props: ILeafletMapProps) {
    super(props);

    MapUtilities.ConfigureLeafLet();
    window.addEventListener('resize', this.debouncedHandleResize.bind(this));

    let mapData = props.currentItem.mapData ? JSON.parse(props.currentItem.mapData) as IMapData : {
      workingAreas: [] as IWorkingArea[],
      stacks: [] as IStackMarker[],
      containerSpots: [] as IContainerSpot[],
      roads: [] as IRoad[],
      textAreas: [] as ITextArea[],
      defaultPosition: App.CurrentUserRegionDefaultPosition.defaultPosition,
      recentZoomLevel: 5
    } as IMapData;

    clearTimeout(this.inactivityTimeout);

    //#region State
    this.state = {
      currentItem: props.currentItem,
      mapWidth: this.getMapWidth(),
      backgroundLayers: [
        BackgroundMaps.Luftfoto,
        BackgroundMaps.TopoDTK25,
        BackgroundMaps.Skærmkort,
        BackgroundMaps.OpenStreetMap,
      ] as ILayer[],
      backgroundLayer: "Skærmkort",
      layers: [
        MapLayers.BeskyttetNatur,
        MapLayers.Fredninger,
        MapLayers.Fortidsminder,
        MapLayers.Beskyttelseslinjer,
        MapLayers.StenOgJorddiger,
        MapLayers.Natura2000,
        MapLayers.HNV,
        MapLayers.Fredskovsarealer,
        MapLayers.Matrikel,
        MapLayers.Vejnavne
      ],
      selectedLayers: [] as string[],
      mapData: mapData,
      currentPosition: props.currentItem.mapData ? mapData.defaultPosition : App.CurrentUserRegionDefaultPosition.defaultPosition as LatLngTuple,
      currentZoomLevel: props.currentItem.mapData ? mapData.recentZoomLevel : 5,
      initialUserPosition: props.currentItem.mapData ? mapData.defaultPosition : App.CurrentUserRegionDefaultPosition.defaultPosition as LatLngTuple,
      activePolylineColor: "#fff",
      activeMarkerIcon: MapUtilities.CreateFixedTaskPositionIcon(props.currentItem.mapData ? mapData.recentZoomLevel : 5),
      isDrawing: false,
      currentDrawingObjectType: DrawingObjectType.None,
      addedGeometryCount: 0,
      showWorkingAreaDialog: false,
      showStackInfoDialog: false,
      showDocumentationDialog: false,
      showTextAreaDialog: false,
      showUserLocationDot: false,
      canEdit: (props.currentItem.status != Status.Approved && props.currentItem.status != Status.Rejected) && (props.currentItem.status != Status.WaitingForApproval || (App.IsUserInRole("DL") || App.IsUserInRole("Purchaser"))),
      loading: true,
    };
  }

  //#region Lifecycle Methods
  componentDidMount() {
    if (isMobileOrTablet()) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const userPosition: LatLngTuple = [latitude, longitude];

          this.setState({
            currentPosition: userPosition,
            initialUserPosition: userPosition,
            currentZoomLevel: 12,
            loading: false,
          });
          
          this.showUserLocationDotWithTimer(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.warn('Could not retrieve device location. Using default position.', error);
          this.setState({ loading: false });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    } else {
      this.setState({ loading: false });
    }
  }

  public componentDidUpdate(prevProps: ILeafletMapProps, prevState: ILeafletMapState): void {
    if (this.mapRef) {
      this.mapRef.leafletElement.off("dblclick", () => { this.completePolygon(); })
    }

    if (prevState.backgroundLayer != this.state.backgroundLayer) {
      this.updateCRS();
    }

    // Handle user location dot visibility changes
    if (prevState.showUserLocationDot !== this.state.showUserLocationDot) {
      if (this.state.showUserLocationDot && this.state.initialUserPosition) {
        this.renderUserPositionDot(this.state.initialUserPosition[0], this.state.initialUserPosition[1]);
      } else {
        this.removeUserPositionDot();
      }
    }
  }

  componentWillUnmount() {
    // Clear timers and remove markers
    if (this.userLocationTimer) {
      clearTimeout(this.userLocationTimer);
    }
    this.removeUserPositionDot();
  }

  private updateCRS(): void {
    if (this.state.backgroundLayer == "Open Street Map") {
      this.mapRef.leafletElement.options.crs = MapUtilities.CRS_OpenStreetMap;
    } else {
      // Set the CRS to the default one
      this.mapRef.leafletElement.options.crs = MapUtilities.CRS;
    }

    this.mapRef.leafletElement._resetView(this.mapRef.leafletElement.getCenter(), this.mapRef.leafletElement.getZoom(), true);
  }

  private onSaveClicked(): void {
    const updatedMapData = this.saveAll();

    this.props.onSaveClicked(updatedMapData, this.state.canEdit);
  }

  public render(): React.ReactElement<ILeafletMapProps> {
    if (this.state.canEdit) {
      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = setTimeout(this.handleInactivity.bind(this), this.inactivityThreshold);
    }

    //#region CommandBar
    const CommandBar_Map: ICommandBarItemProps[] = [
      {
        key: 'drawMap',
        text: 'Tegn område',
        iconProps: { iconName: 'WhiteBoardApp32' },
        disabled: !this.state.canEdit,
        subMenuProps: {
          items: [
            {
              key: 'add_workingArea',
              name: `Arbejdsområde (${this.state.mapData.workingAreas.filter(d => d.type == WorkingAreaType.WorkingArea).length})`,
              onClick: this.addWorkingArea.bind(this)
            },
            {
              key: 'add_line',
              name: `Linje (${this.state.mapData.workingAreas.filter(d => d.type == WorkingAreaType.Polyline).length})`,
              onClick: this.addPolyline.bind(this)
            }
          ]
        }
      },
      {
        key: 'add_fixedTaskPosition',
        text: 'Tilføj opgave GPS-punkt',
        iconProps: { iconName: 'POI' },
        onClick: this.drawFixedTaskPosition.bind(this),
        disabled: !this.state.canEdit || this.state.mapData.fixedTaskPosition != undefined
      },
      {
        key: 'add_stack',
        name: `Tilføj stak (${this.state.mapData.stacks.length})`,
        iconProps: { iconName: 'MapLayers' },
        onClick: this.addStackToMap.bind(this),
        disabled: !this.state.canEdit
      },
      {
        key: 'add_containerSpot',
        name: `Tilføj containerplads (${this.state.mapData.containerSpots.length})`,
        iconProps: { iconName: 'RecycleBin' },
        onClick: this.addContainerSpot.bind(this),
        disabled: !this.state.canEdit
      },
      {
        key: 'add_road',
        text: 'Tilføj vej',
        iconProps: { iconName: 'Car' },
        disabled: !this.state.canEdit,
        subMenuProps: {
          items: [{
            key: 'add_roadAllowed',
            name: `Lastbil vej tilladt (${this.state.mapData.roads.filter(d => d.type == RoadType.Allowed).length})`,
            onClick: this.drawRoadAllowed.bind(this)
          },
          {
            key: 'add_roadProhibited',
            name: `Lastbil vej forbudt (${this.state.mapData.roads.filter(d => d.type == RoadType.Prohibited).length})`,
            onClick: this.drawButton_RoadProhibited.bind(this)
          }
          ]
        }
      },
      {
        key: 'add_text',
        name: `Tilføj tekst (${this.state.mapData.textAreas.length})`,
        iconProps: { iconName: 'InsertTextBox' },
        onClick: this.addTextArea.bind(this),
        disabled: !this.state.canEdit
      }
    ];

    if (isMobileOrTablet()) {
      CommandBar_Map.splice(1, 0, {
        key: 'centerOnLocation',
        text: 'Min position',
        iconProps: { iconName: 'POISolid' },
        onClick: this.centerOnUserLocation.bind(this)
      });
    }

    const CommandBar_Map_FarItems: ICommandBarItemProps[] = [
      {
        key: 'saveOrClose',
        text: this.state.canEdit ? "Gem kort" : "Luk kortet",
        iconProps: { iconName: this.state.canEdit ? 'Save' : 'ChromeClose' },
        className: this.state.canEdit ? styles.buttonSaveAndClose : "",
        onClick: () => {
          if (this.state.canEdit) {
            clearTimeout(this.inactivityTimeout);
          }

          this.onSaveClicked();
        }
      }
    ];

    if (this.state.canEdit) {
      CommandBar_Map_FarItems.splice(0, 0, {
        key: 'closeWithoutSaving',
        text: 'Luk uden at gemme',
        iconProps: { iconName: 'ChromeClose' },
        onClick: () => {
          if (this.state.canEdit) {
            clearTimeout(this.inactivityTimeout);
          }

          this.props.onDismiss(this.state.currentItem)
        }
      });
    }

    //#region Render Map Dialog
    return (<Dialog
      hidden={false}
      modalProps={{ isBlocking: true }}
      minWidth={this.state.mapWidth}
      title="Tegn kort">
      <div>
        <AddressAutoComplete onAddressSelected={(x: number, y: number) => {
          this.setState({ currentPosition: [y, x], currentZoomLevel: 11 });
        }} />
      </div>
      <div className={styles.leafletMap}>
        <div className={styles.leftMenu}>
          {this.renderLeftMenu()}
        </div>
        <div className={styles.seperator}></div>
        <div className={styles.mapContainer}>
          {this.state.loading ? (
            <div className={styles.spinnerContainer}>
              <Spinner size={SpinnerSize.large} label="Henter kort..." />
            </div>
          ) : (
            <>
              <CommandBar className={styles.commandBarMap} items={CommandBar_Map} styles={{ root: { paddingLeft: "0px", height: "38px" } }} farItems={CommandBar_Map_FarItems} />
              <Map onzoomend={this.debouncedOnZoomEnd.bind(this)} zoomControl={true} ref={(ref: any) => { this.mapRef = ref; this.preRenderLeafletMap(); }} minZoom={3} maxZoom={13} bounceAtZoomLimits={true} doubleClickZoom={false} crs={MapUtilities.CRS} className={styles.map} center={this.state.currentPosition} zoom={this.state.currentZoomLevel} scrollWheelZoom={true}>
                {/* <Map onzoomend={this.onZoomEnd.bind(this)} zoomControl={true} ref={(ref : any) => { this.mapRef = ref; this.preRenderLeafletMap(); }} minZoom={3} maxZoom={this.state.backgroundLayer == "Open Street Map" ? 19 : 13} crs={this.state.backgroundLayer == "Open Street Map" ? MapUtilities.CRS_OpenStreetMap : MapUtilities.CRS} bounceAtZoomLimits={true} doubleClickZoom={false} className={styles.map} center={this.state.currentPosition} zoom={this.state.currentZoomLevel} scrollWheelZoom={true}> */}
                <ScaleControl imperial={false} />
                <LayersControl collapsed={false} position="topright">
                  {this.state.backgroundLayers.filter(layer => layer.name == this.state.backgroundLayer).map((layer: ILayer) =>
                    <LayersControl.BaseLayer key={`layer-${layer.name}`} checked={this.state.backgroundLayer === layer.name} name={layer.name}>
                      {layer.type == "wmts" &&
                        <TileLayer
                          minZoom={3}
                          maxZoom={layer.maxZoom}
                          crossOrigin={true}
                          attribution={layer.attribution}
                          url={layer.url} />
                      }
                      {layer.type == "wms" &&
                        <WMSTileLayer
                          layers={layer.layers}
                          minZoom={3}
                          maxZoom={layer.maxZoom}
                          version={layer.version}
                          transparent={layer.transparent}
                          styles={"default"}
                          format={'image/png'}
                          url={layer.url} />
                      }
                    </LayersControl.BaseLayer>
                  )}
                  {this.state.layers.filter(layer => layer.enabled).map((layer: ILayer) =>
                    <LayersControl.Overlay key={`overlay-${layer.name}`} name={layer.name} checked={layer.enabled}>
                      <WMSTileLayer
                        layers={layer.layers}
                        // minZoom={this.minZoomLevelForLayers}
                        transparent={(layer.name == "Matrikel" || layer.name == "Fredskovsarealer") ? "TRUE" as any : true}   //Some WMS api requires TRUE value in uppercase                           
                        format={'image/png'}
                        styles={layer.styles ? layer.styles : ""}
                        // dpi={layer.dpi ? layer.dpi : ""}
                        // map_resolution={layer.map_resolution ? layer.map_resolution : ""}
                        // format_options={layer.format_options ? layer.format_options : ""}
                        // crs={layer.crs ? layer.crs : undefined}
                        url={layer.url} />
                    </LayersControl.Overlay>
                  )}
                </LayersControl>
                <FeatureGroup ref={(ref: any) => { this.groupRef = ref; if (this.props.currentItem.mapData && !this.state.mapData.fixedTaskPosition) this.zoomToBounds(); }} key={`"FeatureGroup_${this.state.addedGeometryCount}`}>
                  <EditControl
                    ref={(ref: any) => { this.editControl = ref; }}
                    position="topleft"
                    draw={{
                      circle: false,
                      circlemarker: false,
                      rectangle: false,
                      polyline: {
                        shapeOptions: {
                          color: this.state.activePolylineColor,
                          opacity: 0.8
                        }
                      },
                      polygon: true,
                      marker: {
                        icon: this.state.activeMarkerIcon
                      },
                    }}
                    onCreated={this._onCreated.bind(this)}
                    onEdited={this._onEdited.bind(this)}
                  />
                  {this.state.mapData.fixedTaskPosition && this.renderFixedTaskPosition()}
                  {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.workingAreas?.map((workingarea: IWorkingArea) => this.renderWorkingArea(workingarea))}
                  {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.roads?.map((road: IRoad) => this.renderRoad(road))}
                  {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.stacks?.map((stack: IStackMarker) => this.renderStack(stack))}
                  {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.containerSpots?.map((marker: IContainerSpot) => this.renderContainerSpot(marker))}
                  {this.state.currentZoomLevel >= this.minZoomLevelForDrawingObjects && this.state.mapData.textAreas?.map((textArea: ITextArea) => this.renderTextArea(textArea))}
                </FeatureGroup>
                {/* <MapCacheLayer /> */}
              </Map>
            </>
          )}
        </div>
        {this.state.showWorkingAreaDialog && this.state.currentWorkingArea &&
          <WorkingAreaDialog currentWorkingArea={this.state.currentWorkingArea} onSaveClicked={(identifier: string, name: string, color: string) => {
            let workingAreas = [...this.state.mapData.workingAreas];
            let index = workingAreas.findIndex(e => e.identifier === identifier);
            workingAreas[index].name = name;
            workingAreas[index].color = color;

            this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, showWorkingAreaDialog: false, currentWorkingArea: undefined });
          }}
          />
        }
        {this.state.showStackInfoDialog && this.state.currentStack &&
          <StackDialog currentStack={this.state.currentStack}
            onSaveClicked={(identifier: string, no: string, size: number, treeSpecices: string, tooltipPosition: Direction) => {
              let stacks = [...this.state.mapData.stacks];
              let index = stacks.findIndex(e => e.identifier === identifier);
              stacks[index].no = no;
              stacks[index].size = size;
              stacks[index].treeSpecices = treeSpecices;
              stacks[index].tooltipPosition = tooltipPosition;

              this.setState({ mapData: { ...this.state.mapData, stacks: stacks }, showStackInfoDialog: false, currentStack: undefined, addedGeometryCount: this.state.addedGeometryCount + 1 });
            }}
            // Save stack values to parent state
            onChanged={(no, size, treeSpecices, tooltipPosition) => {
              this.setState({
                stackDialogValues: { no, size, treeSpecices, tooltipPosition }
              });
            }}
            onDismiss={() => {
              let stacks = this.state.mapData.stacks.filter(
                d => d !== this.state.currentStack
              );

              this.setState({
                mapData: { ...this.state.mapData, stacks: stacks },
                showStackInfoDialog: false,
                currentStack: undefined,
                addedGeometryCount: this.state.addedGeometryCount + 1
              });
            }}
          />
        }
        {this.state.showTextAreaDialog && this.state.currentTextArea &&
          <TextAreaDialog currentTextArea={this.state.currentTextArea} onSaveClicked={(identifier: string, name: string, color: string) => {
            let textAreas = [...this.state.mapData.textAreas];
            let index = textAreas.findIndex(e => e.identifier === identifier);
            textAreas[index].name = name;
            textAreas[index].color = color;

            this.setState({ mapData: { ...this.state.mapData, textAreas: textAreas }, showTextAreaDialog: false, currentTextArea: undefined });
          }}
            onDismiss={() => {
              let textAreas = this.state.mapData.textAreas.filter(
                d => d !== this.state.currentTextArea
              );

              this.setState({
                mapData: { ...this.state.mapData, textAreas: textAreas },
                showTextAreaDialog: false,
                currentTextArea: undefined,
                addedGeometryCount: this.state.addedGeometryCount + 1
              });
            }}
          />
        }
      </div>
    </Dialog>);
  }

  //#region Render Map Elements
  private renderUserPositionDot(lat: number, lng: number) {
    if (!this.mapRef || !this.mapRef.leafletElement || !this.state.showUserLocationDot) return;
    
    // Remove existing user location marker if it exists
    this.removeUserPositionDot();
    
    const dotIcon = L.divIcon({
      className: 'user-position-dot',
      html: '<div style="width:14px;height:14px;border-radius:50%;background:#0078d4;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);"></div>'
    });
    this.userLocationMarker = L.marker([lat, lng], { icon: dotIcon }).addTo(this.mapRef.leafletElement);
  }

  private removeUserPositionDot() {
    if (this.userLocationMarker && this.mapRef && this.mapRef.leafletElement) {
      this.mapRef.leafletElement.removeLayer(this.userLocationMarker);
      this.userLocationMarker = null;
    }
  }

  private showUserLocationDotWithTimer(lat: number, lng: number) {
    // Clear any existing timer
    if (this.userLocationTimer) {
      clearTimeout(this.userLocationTimer);
    }

    // Show the dot
    this.setState({ showUserLocationDot: true }, () => {
      this.renderUserPositionDot(lat, lng);
    });

    // Set timer to hide the dot after specified duration
    this.userLocationTimer = setTimeout(() => {
      this.setState({ showUserLocationDot: false }, () => {
        this.removeUserPositionDot();
      });
    }, this.userLocationVisibleDuration);
  }

  private renderLeftMenu(): JSX.Element {
    return <React.Fragment>
      <div className={styles.leftMenuHeading}>Kortlag</div>
      <List items={this.state.layers} onRenderCell={(item?: ILayer, index?: number, isScrolling?: boolean) => { return this.onRenderLayerListItem(item, index, isScrolling); }} />
      <div className={styles.leftMenuDivider}></div>
      <div className={styles.leftMenuHeading}>Baggrundskort</div>
      <List items={this.state.backgroundLayers} onRenderCell={(item?: ILayer, index?: number, isScrolling?: boolean) => { return this.onRenderBackgroundLayerListItem(item, index, isScrolling); }} />
    </React.Fragment>
  }

  private renderWorkingArea(workingarea: IWorkingArea): JSX.Element {
    // const isDrawingMode = this.state.currentDrawingObjectType !== DrawingObjectType.None;
    
    if (workingarea.type == WorkingAreaType.WorkingArea) {
      // return <Polygon 
      //   onClick={!isDrawingMode ? () => this.setState({ selectedWorkingAreaId: workingarea.identifier }) : undefined} 
      //   editable={workingarea.isEditable} 
      //   weight={this.lineOrBorderWeight} 
      //   attribution={workingarea.identifier} 
      //   key={workingarea.identifier} 
      //   positions={workingarea.coordinates} 
      //   opacity={0.7} 
      //   fillColor={workingarea.color} 
      //   color={workingarea.color == "transparent" ? "red" : workingarea.color}
      //   interactive={!isDrawingMode || workingarea.isEditable}
      // >
      return <Polygon onClick={() => this.setState({ selectedWorkingAreaId: workingarea.identifier })} editable={workingarea.isEditable} weight={this.lineOrBorderWeight} attribution={workingarea.identifier} key={workingarea.identifier} positions={workingarea.coordinates} opacity={0.7} fillColor={workingarea.color} color={workingarea.color == "transparent" ? "red" : workingarea.color} >
        {this.state.selectedWorkingAreaId === workingarea.identifier && (
          <Popup>
            <div style={{ textAlign: "center" }}><b>Arbejdsområde: {workingarea.name}</b></div>
            {this.state.canEdit &&
              <React.Fragment>
                <PrimaryButton className={styles.popupButton} text={"Rediger"} onClick={() => { this.setState({ showWorkingAreaDialog: true, currentWorkingArea: workingarea }); }} />
                <PrimaryButton className={styles.popupButton} text={workingarea.isEditable ? "Gem ændringer" : "Ret område"} onClick={() => {
                  let workingAreas = [...this.state.mapData.workingAreas];
                  let index = workingAreas.findIndex(d => d.identifier == workingarea.identifier);
                  workingAreas[index].isEditable = !workingAreas[index].isEditable;

                  if (workingAreas[index].isEditable === false) {
                    this.drawingStop();
                  } else {
                    // Set active drawing type and force map drawing layer refresh
                    this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, addedGeometryCount: this.state.addedGeometryCount + 1, currentDrawingObjectType: DrawingObjectType.WorkingArea });
                  }
                }} />
                <DefaultButton className={styles.mapPopupDeleteButton} text="Slet" onClick={() => {
                  let workingAreas = this.state.mapData.workingAreas.filter(d => d !== workingarea);
                  this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, addedGeometryCount: this.state.addedGeometryCount + 1 });
                }} />
              </React.Fragment>
            }
          </Popup>)
        }
      </Polygon>
    } else {
      // return <Polyline 
      //   onClick={!isDrawingMode ? () => this.setState({ selectedWorkingAreaId: workingarea.identifier }) : undefined} 
      //   editable={workingarea.isEditable} 
      //   weight={this.lineOrBorderWeight} 
      //   attribution={workingarea.identifier} 
      //   key={workingarea.identifier} 
      //   positions={workingarea.coordinates} 
      //   opacity={0.7} 
      //   fillColor={workingarea.color} 
      //   color={workingarea.color == "transparent" ? "red" : workingarea.color}
      //   interactive={!isDrawingMode || workingarea.isEditable}
      // >
      return <Polyline onClick={() => this.setState({ selectedWorkingAreaId: workingarea.identifier })} editable={workingarea.isEditable} weight={this.lineOrBorderWeight} attribution={workingarea.identifier} key={workingarea.identifier} positions={workingarea.coordinates} opacity={0.7} fillColor={workingarea.color} color={workingarea.color == "transparent" ? "red" : workingarea.color} >
        {this.state.selectedWorkingAreaId === workingarea.identifier && (
          <Popup>
            <div style={{ textAlign: "center" }}><b>Linje: {workingarea.name}</b></div>
            {this.state.canEdit &&
              <React.Fragment>
                <PrimaryButton className={styles.popupButton} text={"Rediger"} onClick={() => { this.setState({ showWorkingAreaDialog: true, currentWorkingArea: workingarea }); }} />
                <PrimaryButton className={styles.popupButton} text={workingarea.isEditable ? "Gem ændringer" : "Ret linje"} onClick={() => {
                  let workingAreas = [...this.state.mapData.workingAreas];
                  let index = workingAreas.findIndex(d => d.identifier == workingarea.identifier);
                  workingAreas[index].isEditable = !workingAreas[index].isEditable;

                  if (workingAreas[index].isEditable === false) {
                    this.drawingStop();
                  } else {
                    // Set active drawing type and force map drawing layer refresh
                    this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, addedGeometryCount: this.state.addedGeometryCount + 1, currentDrawingObjectType: DrawingObjectType.WorkingArea });
                  }
                }} />
                <DefaultButton className={styles.mapPopupDeleteButton} text="Slet" onClick={() => {
                  let workingAreas = this.state.mapData.workingAreas.filter(d => d !== workingarea);
                  this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, addedGeometryCount: this.state.addedGeometryCount + 1 });
                }} />
              </React.Fragment>
            }
          </Popup>)
        }
      </Polyline>
    }
  }

  private renderRoad(road: IRoad): JSX.Element {
    return <Polyline onClick={() => this.setState({ selectedRoadId: road.identifier })} editable={road.isEditable} weight={this.lineOrBorderWeight} attribution={road.identifier} key={road.identifier} positions={road.coordinates} opacity={0.7} color={road.type == RoadType.Allowed ? MapDrawingColors.RoadAllowed : MapDrawingColors.RoadProhibited} >
      {this.state.selectedRoadId === road.identifier && (
        <Popup>
          <div style={{ textAlign: "center" }}><b>{road.type == RoadType.Allowed ? "Lastbil vej tilladt" : "Lastvil vej forbudt"}</b></div>
          {this.state.canEdit &&
            <React.Fragment>
              <PrimaryButton text={road.isEditable ? "Gem ændringer" : "Ret vej"} onClick={() => {
                let roads = [...this.state.mapData.roads];
                let index = roads.findIndex(d => d.identifier == road.identifier);
                roads[index].isEditable = !roads[index].isEditable;

                if (roads[index].isEditable === false) {
                  this.drawingStop();
                } else {
                  // Set active drawing type and force map drawing layer refresh
                  this.setState({ mapData: { ...this.state.mapData, roads: roads }, addedGeometryCount: this.state.addedGeometryCount + 1, currentDrawingObjectType: road.type == RoadType.Allowed ? DrawingObjectType.RoadAllowed : DrawingObjectType.RoadProhibited });
                }
              }} />
              <DefaultButton className={styles.mapPopupDeleteButton} text="Slet" onClick={() => {
                let roads = this.state.mapData.roads.filter(d => d !== road);
                this.setState({ mapData: { ...this.state.mapData, roads: roads }, addedGeometryCount: this.state.addedGeometryCount + 1 });
              }} />
            </React.Fragment>
          }
        </Popup>)
      }
    </Polyline>
  }

  private renderFixedTaskPosition(): JSX.Element {
    if (!this.state.mapData.fixedTaskPosition) {
      return <React.Fragment />
    }

    return <Marker onclick={() => this.setState({ showFixedTaskPositionPopup: true })} ondragend={(ev) => {
      let position = ev.target.getLatLng();

      let taskPosition = this.state.mapData.fixedTaskPosition;

      if (taskPosition != undefined) {
        taskPosition.position = position as LatLng;
        this.setState({ mapData: { ...this.state.mapData, fixedTaskPosition: taskPosition }, addedGeometryCount: this.state.addedGeometryCount + 1 });
      }

    }} draggable={this.state.mapData.fixedTaskPosition.isEditable} icon={MapUtilities.CreateFixedTaskPositionIcon(this.state.currentZoomLevel)} attribution={`fixedTaskPosition`} key={`fixedTaskPosition`} position={this.state.mapData.fixedTaskPosition.position}>
      {this.state.showFixedTaskPositionPopup && (
        <Popup>
          <span>
            Opgave GPS: {this.state.mapData.fixedTaskPosition.position.lat},{this.state.mapData.fixedTaskPosition.position.lng}<br />
            <Link target="_blank" href={`https://www.google.com/maps/search/?api=1&query=${this.state.mapData.fixedTaskPosition.position.lat},${this.state.mapData.fixedTaskPosition.position.lng}`}>Åbn i Google Maps</Link><br />
            {this.state.canEdit &&
              <React.Fragment>
                <PrimaryButton text={this.state.mapData.fixedTaskPosition.isEditable ? "Afslut redigering" : "Rediger"} onClick={() => {
                  let fixedTaskPosition = this.state.mapData.fixedTaskPosition;

                  if (fixedTaskPosition != undefined) {
                    fixedTaskPosition.isEditable = !fixedTaskPosition?.isEditable;

                    if (fixedTaskPosition.isEditable === false) {
                      this.drawingStop();
                    } else {
                      // Set active drawing type and force map drawing layer refresh
                      this.setState({ mapData: { ...this.state.mapData, fixedTaskPosition: fixedTaskPosition }, addedGeometryCount: this.state.addedGeometryCount + 1, currentDrawingObjectType: DrawingObjectType.FixedTaskPosition });
                    }
                  }

                }} />
                <DefaultButton className={styles.mapPopupDeleteButton} text="Slet" onClick={() => {

                  this.setState({ mapData: { ...this.state.mapData, fixedTaskPosition: undefined }, addedGeometryCount: this.state.addedGeometryCount + 1 });
                }} />
              </React.Fragment>
            }
          </span>
        </Popup>)
      }
    </Marker>
  }

  private renderStack(stack: IStackMarker): JSX.Element {
    let icon: L.Icon<L.IconOptions> = MapUtilities.CreateStackIcon(this.state.currentZoomLevel, stack.tooltipPosition);

    return <Marker onclick={() => this.setState({ selectedStackId: stack.identifier })} ondragend={(ev) => {
      let position = ev.target.getLatLng();

      let stacks = [...this.state.mapData.stacks];
      let index = stacks.findIndex(e => e.identifier === stack.identifier);
      stacks[index].position = position as LatLng;

      this.setState({ mapData: { ...this.state.mapData, stacks: stacks }, addedGeometryCount: this.state.addedGeometryCount + 1 });
    }}
      draggable={stack.isEditable} icon={icon} attribution={stack.identifier} key={stack.identifier} position={stack.position}>
      <Tooltip permanent={true} direction={stack.tooltipPosition ?? "auto"} opacity={0.5}>
        <div>
          <div>Nr: {stack.no}</div>
          <div>{stack.size} rm</div>
          <div>{stack.treeSpecices}</div>
        </div>
      </Tooltip>
      {this.state.canEdit && this.state.selectedStackId === stack.identifier &&
        <Popup>
          <React.Fragment>
            <PrimaryButton className={styles.popupButton} text={"Rediger"} onClick={() => { this.setState({ showStackInfoDialog: true, currentStack: stack }); }} />
            <PrimaryButton className={styles.popupButton} text={stack.isEditable ? "Gem ændringer" : "Flyt stak"} onClick={() => {
              let stacks = [...this.state.mapData.stacks];
              let index = stacks.findIndex(d => d.identifier == stack.identifier);
              stacks[index].isEditable = !stacks[index].isEditable;

              if (stacks[index].isEditable === false) {
                this.drawingStop();
              } else {
                // Set active drawing type and force map drawing layer refresh
                this.setState({ mapData: { ...this.state.mapData, stacks: stacks }, addedGeometryCount: this.state.addedGeometryCount + 1, currentDrawingObjectType: DrawingObjectType.Stack });
              }
            }} />
            <DefaultButton className={styles.mapPopupDeleteButton} text="Slet" onClick={() => {
              let stacks = this.state.mapData.stacks.filter(d => d !== stack);
              this.setState({ mapData: { ...this.state.mapData, stacks: stacks }, addedGeometryCount: this.state.addedGeometryCount + 1 });
            }} />
          </React.Fragment>
        </Popup>
      }
    </Marker>
  }

  private renderContainerSpot(containerSpot: IContainerSpot): JSX.Element {
    return <Marker onclick={() => this.setState({ selectedContainerSpotId: containerSpot.identifier })} ondragend={(ev) => {
      let position = ev.target.getLatLng();

      let containerSpots = [...this.state.mapData.containerSpots];
      let index = containerSpots.findIndex(e => e.identifier === containerSpot.identifier);
      containerSpots[index].position = position as LatLng;

      this.setState({ mapData: { ...this.state.mapData, containerSpots: containerSpots }, addedGeometryCount: this.state.addedGeometryCount + 1 });
    }} draggable={containerSpot.isEditable} icon={MapUtilities.CreateContainerspotIcon(this.state.currentZoomLevel)} attribution={containerSpot.identifier} key={containerSpot.identifier} position={containerSpot.position}>
      {this.state.selectedContainerSpotId === containerSpot.identifier && (
        <Popup>
          <div style={{ textAlign: "center" }}><b>Containerplads</b></div>
          {this.state.canEdit &&
            <React.Fragment>
              <PrimaryButton text={containerSpot.isEditable ? "Gem ændringer" : "Flyt containerplads"} onClick={() => {
                let containerSpots = [...this.state.mapData.containerSpots];
                let index = containerSpots.findIndex(d => d.identifier == containerSpot.identifier);
                containerSpots[index].isEditable = !containerSpots[index].isEditable;

                if (containerSpots[index].isEditable === false) {
                  this.drawingStop();
                } else {
                  // Set active drawing type and force map drawing layer refresh
                  this.setState({ mapData: { ...this.state.mapData, containerSpots: containerSpots }, addedGeometryCount: this.state.addedGeometryCount + 1, currentDrawingObjectType: DrawingObjectType.ContainerSpot });
                }
              }} />
              <DefaultButton className={styles.mapPopupDeleteButton} text="Slet" onClick={() => {
                let containerSpots = this.state.mapData.containerSpots.filter(d => d !== containerSpot);
                this.setState({ mapData: { ...this.state.mapData, containerSpots: containerSpots }, addedGeometryCount: this.state.addedGeometryCount + 1 });
              }} />
            </React.Fragment>
          }
        </Popup>)
      }
    </Marker>
  }

  private renderTextArea(textArea: ITextArea): JSX.Element {
    if (!textArea.name) {
      return <React.Fragment />;
    }

    return <Marker onclick={() => this.setState({ selectedTextAreaId: textArea.identifier })} ondragend={(ev) => {
      let position = ev.target.getLatLng();

      let textAreas = [...this.state.mapData.textAreas];
      let index = textAreas.findIndex(e => e.identifier === textArea.identifier);
      textAreas[index].position = position as LatLng;

      this.setState({ mapData: { ...this.state.mapData, textAreas: textAreas }, addedGeometryCount: this.state.addedGeometryCount + 1 });
    }} draggable={textArea.isEditable} icon={MapUtilities.CreateTextIcon(`<div style="display:inline-block; color:#fff; padding:5px; white-space:nowrap; opacity: 0.7; background-color:${textArea.color}">${textArea.name}</div>`)} attribution={textArea.identifier} key={textArea.identifier} position={textArea.position}>
      {this.state.selectedTextAreaId === textArea.identifier && (
        <Popup>
          {this.state.canEdit &&
            <React.Fragment>
              <PrimaryButton className={styles.popupButton} text={"Rediger"} onClick={() => { this.setState({ showTextAreaDialog: true, currentTextArea: textArea }); }} />
              <PrimaryButton className={styles.popupButton} text={textArea.isEditable ? "Gem ændringer" : "Flyt tekst"} onClick={() => {
                let textAreas = [...this.state.mapData.textAreas];
                let index = textAreas.findIndex(d => d.identifier == textArea.identifier);
                textAreas[index].isEditable = !textAreas[index].isEditable;

                if (textAreas[index].isEditable === false) {
                  this.drawingStop();
                } else {
                  // Set active drawing type and force map drawing layer refresh
                  this.setState({ mapData: { ...this.state.mapData, textAreas: textAreas }, addedGeometryCount: this.state.addedGeometryCount + 1, currentDrawingObjectType: DrawingObjectType.TextArea });
                }
              }} />
              <DefaultButton className={styles.mapPopupDeleteButton} text="Slet" onClick={() => {
                let textAreas = this.state.mapData.textAreas.filter(d => d !== textArea);
                this.setState({ mapData: { ...this.state.mapData, textAreas: textAreas }, addedGeometryCount: this.state.addedGeometryCount + 1 });
              }} />
            </React.Fragment>
          }
        </Popup>)
      }
    </Marker>
  }

  //#region Methods
  private preRenderLeafletMap() {
    if (this.mapRef != null) {
      if (this.state.isDrawing && this.state.currentDrawingObjectType == DrawingObjectType.WorkingArea) {
        this.mapRef.leafletElement.on("dblclick", () => { this.completePolygon(); })
      }

      this.mapRef.leafletElement.options.maxZoom = this.state.backgroundLayer == "Open Street Map" ? 19 : 13;
    }
  }

  private centerOnUserLocation(): void {
    const { initialUserPosition } = this.state;

    if (initialUserPosition) {
      this.setState({
        currentPosition: initialUserPosition,
        currentZoomLevel: 12,
      });

      if (this.mapRef && this.mapRef.leafletElement) {
        this.mapRef.leafletElement.setView(initialUserPosition, 12);
      }

      // Show the user location dot with timer
      this.showUserLocationDotWithTimer(initialUserPosition[0], initialUserPosition[1]);

      console.log(`Map centered on initial user location. Latitude: ${initialUserPosition[0]}, Longitude: ${initialUserPosition[1]}`);
    } else {
      console.warn("Initial user position is not available.");
      alert("Din position kunne ikke hentes. Prøv igen senere.");
    }
  }

  private zoomToBounds() {
    if (this.mapRef != null && this.groupRef != null) {
      //Zoom to fit outer bounds of elements on map
      const map = this.mapRef.leafletElement;
      const group = this.groupRef.leafletElement;
      const bounds = group.getBounds();

      if (bounds.isValid()) {
        map.fitBounds(bounds);
      }
    }
  }

  private completePolygon() {
    if (this.editControl.leafletElement._toolbars.draw._modes.polygon.handler._markers) {
      if (this.editControl.leafletElement._toolbars.draw._modes.polygon.handler._markers.length >= 3) {
        this.editControl.leafletElement._toolbars.draw._modes.polygon.handler.completeShape();

        this.setState({
          isDrawing: false
        });
      }
    }
  }

  private debouncedOnZoomEnd = debounce(() => {
    if (this.mapRef && this.mapRef.leafletElement) {
      var currentZoom = this.mapRef.leafletElement.getZoom();
      this.setState({ currentZoomLevel: currentZoom });
    }
  }, 200);

  private handleLayerItemClick(index: number) {
    let newState = update(this.state, {
      layers: {
        [index]: {
          enabled: {
            $set: !this.state.layers[index].enabled
          }
        }
      }
    });
    this.setState(newState);
  }

  private onRenderLayerListItem(item?: ILayer, index?: number, isScrolling?: boolean): JSX.Element {
    const isActive = item?.enabled;
    return (
      <div className={styles.leftMenuItem} >
        <div className={styles.leftMenuItemRow} data-is-focusable={true} title={item?.name + ""} onClick={() => this.handleLayerItemClick(index as number)}>
          <Icon iconName={isActive ? 'RedEye' : 'Hide'} />
          <span>{item?.name + ''}</span>
        </div>
        {item?.signature &&
          <div className={styles.leftMenuItemMore} onClick={() => {
            let newState = update(this.state, {
              layers: {
                [index as number]: {
                  showSignature: {
                    $set: !item?.showSignature
                  }
                }
              }
            });

            this.setState(newState);
          }} >
            <Icon id={`hdmore_icon_${index}`} iconName={'More'} />
            {item?.showSignature &&
              <Callout
                target={`#hdmore_icon_${index}`}
                directionalHint={DirectionalHint.rightCenter}
              >
                <img src={`/images/${item.signature}`} />
              </Callout>
            }
          </div>
        }
      </div>
    );
  }

  private onRenderBackgroundLayerListItem(item?: ILayer, index?: number, isScrolling?: boolean): JSX.Element {
    const isActive = this.state.backgroundLayer === item?.name;
    return (
      <div className={styles.leftMenuItem} >
        <div className={styles.leftMenuItemRow} data-is-focusable={true} title={item?.name + ""} onClick={() => {
          let zoomLevel = this.state.currentZoomLevel;

          if (item && this.state.currentZoomLevel > item.maxZoom) {
            zoomLevel = item.maxZoom;
          }

          this.setState({ backgroundLayer: item?.name + "", backgroundLayers: [...this.state.backgroundLayers], currentZoomLevel: zoomLevel });
        }}>
          <Icon iconName={isActive ? 'RedEye' : 'Hide'} />
          <span>{item?.name + ''}</span>
        </div>
      </div>
    );
  }

  private _onCreated(e: any) {
    let data = e.layer.toGeoJSON();

    switch (this.state.currentDrawingObjectType) {
      case DrawingObjectType.WorkingArea:
        {
          let workingAreas = [...this.state.mapData.workingAreas];
          let newWorkingArea = { type: WorkingAreaType.WorkingArea, identifier: `polygon-workingarea-${Utilities.NewGuid()}`, isEditable: false, coordinates: data.geometry.coordinates[0].map((c) => { return { lat: c[1], lng: c[0] } }) } as IWorkingArea;
          workingAreas.push(newWorkingArea);

          this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, addedGeometryCount: this.state.addedGeometryCount + 1, currentWorkingArea: newWorkingArea, showWorkingAreaDialog: true });
          break;
        }
      case DrawingObjectType.Polyline:
        {
          let workingAreas = [...this.state.mapData.workingAreas];
          let newWorkingArea = { type: WorkingAreaType.Polyline, identifier: `polyline-workingarea-${Utilities.NewGuid()}`, isEditable: false, coordinates: data.geometry.coordinates.map((c) => { return { lat: c[1], lng: c[0] } }) } as IWorkingArea;
          workingAreas.push(newWorkingArea);

          this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, addedGeometryCount: this.state.addedGeometryCount + 1, currentWorkingArea: newWorkingArea, showWorkingAreaDialog: true });
          break;
        }
      case DrawingObjectType.ContainerSpot:
        let containerSpots = [...this.state.mapData.containerSpots];
        containerSpots.push({ identifier: `marker-containerspot-${Utilities.NewGuid()}`, isEditable: false, position: { lat: data.geometry.coordinates[1], lng: data.geometry.coordinates[0] } } as IContainerSpot);

        this.setState({ mapData: { ...this.state.mapData, containerSpots: containerSpots }, addedGeometryCount: this.state.addedGeometryCount + 1 });
        break;
      case DrawingObjectType.Stack:
        let stacks = [...this.state.mapData.stacks];
        let newStack = { identifier: `marker-stack-${Utilities.NewGuid()}`, isEditable: false, position: { lat: data.geometry.coordinates[1], lng: data.geometry.coordinates[0] } as LatLng, size: 0 } as IStackMarker;
        stacks.push(newStack);

        this.setState({ mapData: { ...this.state.mapData, stacks: stacks }, addedGeometryCount: this.state.addedGeometryCount + 1, currentStack: newStack, showStackInfoDialog: true });
        break;
      case DrawingObjectType.RoadAllowed:
        let roadsAllowed = [...this.state.mapData.roads];
        roadsAllowed.push({ identifier: `polyline-road-allowed-${Utilities.NewGuid()}`, isEditable: false, type: RoadType.Allowed, coordinates: data.geometry.coordinates.map((c) => { return { lat: c[1], lng: c[0] } }) });

        this.setState({ mapData: { ...this.state.mapData, roads: roadsAllowed }, addedGeometryCount: this.state.addedGeometryCount + 1 });
        break;
      case DrawingObjectType.RoadProhibited:
        let roadsProhibited = [...this.state.mapData.roads];
        roadsProhibited.push({ identifier: `polyline-road-prohibited-${Utilities.NewGuid()}`, isEditable: false, type: RoadType.Prohibited, coordinates: data.geometry.coordinates.map((c) => { return { lat: c[1], lng: c[0] } }) });

        this.setState({ mapData: { ...this.state.mapData, roads: roadsProhibited }, addedGeometryCount: this.state.addedGeometryCount + 1 });
        break;
      case DrawingObjectType.FixedTaskPosition:
        this.setState({ mapData: { ...this.state.mapData, fixedTaskPosition: { isEditable: false, position: { lat: data.geometry.coordinates[1], lng: data.geometry.coordinates[0] } as LatLng } }, addedGeometryCount: this.state.addedGeometryCount + 1 });
        break;
      case DrawingObjectType.TextArea:
        let textareas = [...this.state.mapData.textAreas];
        let newTextArea = { identifier: `marker-textarea-${Utilities.NewGuid()}`, isEditable: false, position: { lat: data.geometry.coordinates[1], lng: data.geometry.coordinates[0] } } as ITextArea;
        textareas.push(newTextArea);

        this.setState({ mapData: { ...this.state.mapData, textAreas: textareas }, addedGeometryCount: this.state.addedGeometryCount + 1, currentTextArea: newTextArea, showTextAreaDialog: true });
        break;
    }
  }

  private _onEdited(e: any) {
    e.layers.eachLayer(a => {
      let data = a.toGeoJSON();
      const identifier = a.options.attribution;

      if (identifier.indexOf("marker-stack") == 0) {
        let stacks = [...this.state.mapData.stacks];
        let index = stacks.findIndex(e => e.identifier === identifier);
        stacks[index].position = { lat: data.geometry.coordinates[1], lng: data.geometry.coordinates[0] } as LatLng;

        this.setState({ mapData: { ...this.state.mapData, stacks: stacks }, addedGeometryCount: this.state.addedGeometryCount + 1 });
      } else if (identifier.indexOf("marker-containerspot") == 0) {
        let containerSpots = [...this.state.mapData.containerSpots];
        let index = containerSpots.findIndex(e => e.identifier === identifier);
        containerSpots[index].position = { lat: data.geometry.coordinates[1], lng: data.geometry.coordinates[0] } as LatLng;

        this.setState({ mapData: { ...this.state.mapData, containerSpots: containerSpots }, addedGeometryCount: this.state.addedGeometryCount + 1 });
      } else if (identifier.indexOf("polygon-workingarea") == 0) {
        let workingAreas = [...this.state.mapData.workingAreas];
        let index = workingAreas.findIndex(e => e.identifier === identifier);

        let coordinates = [] as LatLng[];
        for (var i = 0; i < data.geometry.coordinates.length; i++) {
          for (var j = 0; j < data.geometry.coordinates[i].length; j++) {
            coordinates.push({ lat: data.geometry.coordinates[i][j][1], lng: data.geometry.coordinates[i][j][0] } as LatLng);
          }
        }

        workingAreas[index].coordinates = coordinates;

        this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, addedGeometryCount: this.state.addedGeometryCount + 1 });
      } else if (identifier.indexOf("polyline-road") == 0) {
        let roads = [...this.state.mapData.roads];
        let index = roads.findIndex(e => e.identifier === identifier);

        let coordinates = [] as LatLng[];
        for (var i = 0; i < data.geometry.coordinates.length; i++) {
          coordinates.push({ lat: data.geometry.coordinates[i][1], lng: data.geometry.coordinates[i][0] } as LatLng);
        }

        roads[index].coordinates = coordinates;

        this.setState({ mapData: { ...this.state.mapData, roads: roads }, addedGeometryCount: this.state.addedGeometryCount + 1 });
      } else if (identifier.indexOf("polyline-workingarea") == 0) {
        let workingAreas = [...this.state.mapData.workingAreas];
        let index = workingAreas.findIndex(e => e.identifier === identifier);

        let coordinates = [] as LatLng[];
        for (var i = 0; i < data.geometry.coordinates.length; i++) {
          coordinates.push({ lat: data.geometry.coordinates[i][1], lng: data.geometry.coordinates[i][0] } as LatLng);
        }

        workingAreas[index].coordinates = coordinates;

        this.setState({ mapData: { ...this.state.mapData, workingAreas: workingAreas }, addedGeometryCount: this.state.addedGeometryCount + 1 });
      } else if (identifier.indexOf("marker-textarea") == 0) {
        let textAreas = [...this.state.mapData.textAreas];
        let index = textAreas.findIndex(e => e.identifier === identifier);

        textAreas[index].position = { lat: data.geometry.coordinates[1], lng: data.geometry.coordinates[0] } as LatLng;

        this.setState({ mapData: { ...this.state.mapData, textAreas: textAreas }, addedGeometryCount: this.state.addedGeometryCount + 1 });
      }
    });
  }

  private addWorkingArea() {
    this.setState({
      isDrawing: true,
      currentDrawingObjectType: DrawingObjectType.WorkingArea
    }, () => {
      this.editControl.leafletElement._toolbars.draw._modes.polygon.handler.enable();
    });
  }

  private addPolyline() {
    this.setState({
      activePolylineColor: MapDrawingColors.Polyline,
      isDrawing: true,
      currentDrawingObjectType: DrawingObjectType.Polyline
    }, () => {
      this.editControl.leafletElement._toolbars.draw._modes.polyline.handler.enable();
    });
  }

  private addStackToMap() {
    this.setState({
      currentDrawingObjectType: DrawingObjectType.Stack,
      activeMarkerIcon: MapUtilities.CreateStackIcon(this.state.currentZoomLevel)
    }, () => {
      this.editControl.leafletElement._toolbars.draw._modes.marker.handler.enable();
    });
  }

  private addContainerSpot() {
    this.setState({
      currentDrawingObjectType: DrawingObjectType.ContainerSpot,
      activeMarkerIcon: MapUtilities.CreateContainerspotIcon(this.state.currentZoomLevel),
    }, () => {
      this.editControl.leafletElement._toolbars.draw._modes.marker.handler.enable();
    });
  }

  private drawRoadAllowed() {
    this.setState({
      activePolylineColor: MapDrawingColors.RoadAllowed,
      isDrawing: true,
      currentDrawingObjectType: DrawingObjectType.RoadAllowed
    }, () => {
      this.editControl.leafletElement._toolbars.draw._modes.polyline.handler.enable();
    });
  }

  private drawButton_RoadProhibited() {
    this.setState({
      activePolylineColor: MapDrawingColors.RoadProhibited,
      isDrawing: true,
      currentDrawingObjectType: DrawingObjectType.RoadProhibited
    }, () => {
      this.editControl.leafletElement._toolbars.draw._modes.polyline.handler.enable();
    });
  }

  private drawFixedTaskPosition() {
    this.setState({
      activeMarkerIcon: MapUtilities.CreateFixedTaskPositionIcon(this.state.currentZoomLevel),
      currentDrawingObjectType: DrawingObjectType.FixedTaskPosition
    }, () => {
      this.editControl.leafletElement._toolbars.draw._modes.marker.handler.enable();
    });
  }

  private addTextArea() {
    this.setState({
      activeMarkerIcon: MapUtilities.none,
      currentDrawingObjectType: DrawingObjectType.TextArea
    }, () => {
      this.editControl.leafletElement._toolbars.draw._modes.marker.handler.enable();
    });
  }

  private drawingStop() {
    switch (this.state.currentDrawingObjectType) {
      case DrawingObjectType.WorkingArea:
        this.editControl.leafletElement._toolbars.draw._modes.polygon.handler.disable();
        this.editControl.leafletElement._toolbars.draw._modes.polyline.handler.disable();
        break;
      case DrawingObjectType.RoadAllowed:
      case DrawingObjectType.RoadProhibited:
        //this.editControl.leafletElement._toolbars.draw._modes.polyline.handler.completeShape();
        this.editControl.leafletElement._toolbars.draw._modes.polyline.handler.disable();
        break;
      case DrawingObjectType.Stack:
        this.editControl.leafletElement._toolbars.edit._modes.edit.handler.enable();
        break;
    }

    this.editControl.leafletElement._toolbars.edit._modes.edit.handler.save();
    this.editControl.leafletElement._toolbars.edit._modes.edit.handler.disable();

    this.setState({ isDrawing: false, currentDrawingObjectType: DrawingObjectType.None, addedGeometryCount: this.state.addedGeometryCount + 1 });
  }

  private saveAll(): IMapData {
    this.drawingStop();

    let mapData = this.state.mapData;

    mapData.workingAreas.forEach((item, i, self) => item.isEditable = false);
    mapData.roads.forEach((item, i, self) => item.isEditable = false);
    mapData.stacks.forEach((item, i, self) => item.isEditable = false);
    mapData.containerSpots.forEach((item, i, self) => item.isEditable = false);
    mapData.textAreas.forEach((item, i, self) => item.isEditable = false);

    if (mapData.fixedTaskPosition) {
      mapData.fixedTaskPosition.isEditable = false;
    }

    // Persist geo json for drawn objects
    mapData.geoJson = [] as any[];

    this.mapRef.leafletElement.eachLayer((layer) => {
      // All drawn objects resides on layers without URL and with attribution set
      if (layer._url === undefined && layer.options.attribution) {
        try {
          let geoJson = layer.toGeoJSON();

          if (geoJson.type == "Feature") {
            let props = { "type": this.translateAttribution(layer.options.attribution), "info": {} };

            if (props.type == "Stack") {
              let stack: IStackMarker = mapData.stacks.filter(d => d.position == layer.options.position)[0];
              if (stack) {
                props.info = { "No": stack.no, "TreeSpecices": stack.treeSpecices, "Volume": stack.size };
              }
            }

            if (props.type == "WorkingArea" || props.type == "Line") {
              let wa: IWorkingArea = mapData.workingAreas.filter(d => d.identifier == layer.options.attribution)[0];
              if (wa) {
                props.info = { "StandInfo": wa.name };
              }
            }

            if (props.type == "Text") {
              let ta: ITextArea = mapData.textAreas.filter(d => d.identifier == layer.options.attribution)[0];
              if (ta) {
                props.info = { "Value": ta.name };
              }
            }

            geoJson.properties = props;

            mapData.geoJson.push(geoJson);
          }
        } catch (e) {
          alert('Der er sket en fejl under lagring af kortdata. Prøv venligst igen.');
        };
      }
    });

    //If we have a fixed task position, use this as the default position when opening the map in the future
    if (this.state.mapData.fixedTaskPosition) {
      this.state.mapData.defaultPosition = [
        this.state.mapData.fixedTaskPosition.position.lat,
        this.state.mapData.fixedTaskPosition.position.lng
      ] as LatLngTuple;
    } else if (
      this.state.mapData.workingAreas &&
      this.state.mapData.workingAreas.length > 0 &&
      this.state.mapData.workingAreas[0].coordinates &&
      this.state.mapData.workingAreas[0].coordinates.length > 0
    ) {
      this.state.mapData.defaultPosition = [
        this.state.mapData.workingAreas[0].coordinates[0].lat,
        this.state.mapData.workingAreas[0].coordinates[0].lng
      ] as LatLngTuple;
    }

    // let mapData = this.state.mapData;
    mapData.recentZoomLevel = this.state.currentZoomLevel;
    mapData.stackSum = this.state.mapData.stacks?.reduce((n, { size }) => n + size, 0);


    this.setState({ mapData: mapData });

    return mapData;
  }

  private translateAttribution(attribution: string): string {
    if (attribution == "fixedTaskPosition") { return "TaskGPS"; }
    else if (attribution.indexOf("polygon-workingarea") > -1) { return "WorkingArea"; }
    else if (attribution.indexOf("polyline-workingarea") > -1) { return "WorkingArea"; } //Send Line as WorkingArea too
    else if (attribution.indexOf("polyline-road-allowed") > -1) { return "RoadAllowed"; }
    else if (attribution.indexOf("polyline-road-prohibited") > -1) { return "RoadProhibited"; }
    else if (attribution.indexOf("marker-stack") > -1) { return "Stack"; }
    else if (attribution.indexOf("marker-containerspot") > -1) { return "ContainerSpot"; }
    else if (attribution.indexOf("marker-textarea") > -1) { return "Text"; }
    else { return "N/A"; }
  }

  private getMapWidth() {
    return window.innerWidth * 0.95;
  }

  private debouncedHandleResize = debounce(() => {
    this.setState({ mapWidth: this.getMapWidth() });
  }, 200);

  private handleInactivity() {
    const updatedMapData = this.saveAll();

    this.props.onSaveDueToInactivity(updatedMapData);

    // Disable until next render()
    clearTimeout(this.inactivityTimeout);
  }
}